<template>
    <vue-select
        ref="vueSelectSearchBilling"
        placeholder="Search invoices, estimates or returns"
        label="iname"
        :options="options"
        :clearable="false"
        :closeOnSelect="false"
        :clearSearchOnBlur="() => false"
        :filterable="false"
        @search="search"
        @input="onSelect"
        @close="onClose"
        class="search-billing"
    >
        <template v-slot:option="{ label }">
            <b v-html="`${label}`"></b>
        </template>

        <template v-slot:no-options="{ search }">
            <div class="no-opt">
                <span v-if="search === ''">
                    Type to search
                </span>
                <span v-else-if="isPreloading">
                    Loading...
                </span>
                <span v-else>
                    No result
                </span>
            </div>
        </template>

        <template v-slot:open-indicator>
            <!-- this empty div is intentional, do not remove. this is for a "replacement" to default arrow -->
            <div></div>
        </template>

        <template v-slot:spinner="{ loading }">
            <img
                v-if="loading"
                class="spinner"
                src="/apps/assets/img/ajax-spinner.gif"
            />
        </template>

        <template v-slot:list-footer>
            <li ref="footerLoader" class="footerloader" v-if="hasNextPage">
                Loading...
            </li>
        </template>
    </vue-select>
</template>

<script>
import vueSelect from "vue-select";
import { debounce, concat, isEmpty } from "lodash";

const TOTAL_PERPAGE = 20;
const BILLING_SEARCH_URI = `${process.env.BARRAMUNDI}billing/search-billing`;
const baseURLci = process.env.APPS;
/**
 * @name VetSelectBilling
 * This component is the replicate of vet-select-patient
 * TODO: Based on the vet-select componentns for all modules, seem we put the same method in to a mixin
 */
export default {
    components: {
        vueSelect
    },
    props: {
        focusOnload: {
            default: false,
            type: Boolean
        }
    },
    watch: {
        options(options = []) {
            let maxLength = 0;
            let ul = document.querySelector(".search-billing ul");
            if (options.length === 0) {
                ul.style.width = "";
                ul.style.left = "";
                ul.style.right = "";
                ul.style.borderTopLeftRadius = "";
                ul.style.borderTopRightRadius = "";
            } else {
                for (let option of options) {
                    option.label = `${option.iname} (${this._setDateDisplay(
                        option.idate
                    )})`;

                    let nameLength = option.label.length;
                    if (nameLength > maxLength) {
                        maxLength = nameLength;
                    }
                }

                ul.style.width = `${maxLength * 8}px`;
                ul.style.left = "unset";
                ul.style.right = "0";
                ul.style.borderTopLeftRadius = "5px";
                ul.style.borderTopRightRadius = "5px";
            }
        }
    },
    data() {
        return {
            query: "",
            options: [],
            observer: null,
            hasNextPage: false,
            isPreloading: false,
            currentPage: 1
        };
    },
    mounted() {
        this.observer = new IntersectionObserver(this.infiniteScroll);

        if (this.focusOnload) {
            this.focus();
        }
    },
    methods: {
        focus() {
            this.$refs.vueSelectSearchBilling.$el
                .querySelector("input")
                .focus();
        },
        search(value, loading) {
            this.hasNextPage = false;
            this.currentPage = 1;
            this.isPreloading = true;
            this.query = value;

            if (value === "") {
                this.isPreloading = false;
                this.options = [];
                this.hasNextPage = false;
                return;
            }

            this.fetchOptions(value, this, loading);
        },
        fetchOptions: debounce(async (value, self, loading) => {
            loading(true);

            const res = await self.$http.get(BILLING_SEARCH_URI, {
                params: {
                    query: value,
                    page: self.currentPage
                }
            });
            let response = res.data.response;
            const results = response.resources;

            if (response.message.code === 200) {
                self.hasNextPage = response.meta.total >= TOTAL_PERPAGE;
                self.options = Object.values(results);
                self.currentPage++;
            } else {
                self.options = [];
            }
            loading(false);

            self.isPreloading = false;
            await self.registerFootLoader();
        }, 350),
        onSelect(value) {
            if (!value) {
                return;
            }
            let submodule = "";
            if (value.iname.toLowerCase().indexOf("invoice #") !== -1) {
                submodule = "invoice";
            } else if (value.iname.toLowerCase().indexOf("estimate #") !== -1) {
                submodule = "estimate";
            } else if (value.iname.toLowerCase().indexOf("return #") !== -1) {
                submodule = "return";
            } else {
                console.error("Error: URL not found");
                return;
            }

            this.$router.push({
                name: submodule,
                params: {
                    id: value.id
                }
            });

            this.onClose();
            window.scrollTo(0, 0); // reset scroll position
        },
        async registerFootLoader() {
            if (this.hasNextPage) {
                await this.$nextTick();
                this.observer.observe(this.$refs.footerLoader);
            }
        },
        onOpen() {
            this.registerFootLoader();
        },
        onClose() {
            this.observer.disconnect();
            this.hasNextPage = false;
            this.$refs.vueSelectSearchBilling.clearSelection();
            this.$refs.vueSelectSearchBilling.$el.querySelector("input").blur();
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            const self = this;

            // references: https://vue-select.org/guide/infinite-scroll.html
            if (isIntersecting && this.hasNextPage) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.$nextTick();

                this.fetchNextPage(async () => {
                    await self.$nextTick();
                    ul.scrollTop = scrollTop; // keep scroll position after we append the options
                });
            }
        },
        async fetchNextPage(callback) {
            if (!this.hasNextPage) {
                return;
            }
            let self = this;

            const res = await this.$http.get(BILLING_SEARCH_URI, {
                params: {
                    page: self.currentPage,
                    query: self.query
                }
            });

            let response = res.data.response;
            const results = response.resources;
            if (response.message.code === 200) {
                self.options = concat(self.options, Object.values(results));
                self.hasNextPage = !isEmpty(results);
                self.currentPage++;
                if (callback) {
                    callback();
                }
            } else {
                self.hasNextPage = false;
            }
        }
    }
};
</script>

<style scoped>
.no-opt {
    padding: 0 5px;
}
.footerloader {
    padding: 3px 20px;
    font-style: italic;
}
</style>
